import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { RouteConfig, Router } from "aurelia-router";
import { RoutesService } from "../../../routes/routes";
import * as Enumerable from "linq";
import { Disposable } from "aurelia-framework";
import { BindingSignaler } from "aurelia-templating-resources";
import '../../../helpers/scroll-into-view';
import { ClaimsStep } from "../../../pipelines/authentication/claims.pipeline";
import { MenuTab } from "../../../models/basics/basics.models";
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import * as DOMPurify from "dompurify";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightshipQiMessages } from "../../../configuration/rightshipqi-event-messages.constants";

//Load styles
require("../../../styles/attachment-viewer-client/core-attachment-viewer-client.scss");

@autoinject()
@transient()
export class layoutAttachmentViewer {

    private _router: Router;
    private _subscriptions: Disposable[] = [];
    public selectedRoute: RouteConfig = { route: null };
    public routes: RouteConfig[] = [];
    public config: any;
    public documentClick: EventListenerOrEventListenerObject
    public paste: EventListenerOrEventListenerObject
    public contentShowing: boolean;
    public showDatePicker: boolean = false;
    public menu: MenuTab[] = null;
    public nonNavigationRoutes: RouteConfig[] = null;

    constructor(private eventAggregator: EventAggregator,
        private routesService: RoutesService, private bindingSignaler: BindingSignaler,
        private googleAnalyticsService: GoogleAnalyticsService,
        private dialogClientService: DialogClientService) {

        this.documentClick = e => {
            if (!(<any>e).propagationStopped) {
                eventAggregator.publish(Messages.documentClick, { source: "document" });
            }
        };

        this.paste = (e: any) => {
            if (!e.propagationStopped) {
                const items = ((e.clipboardData || (e.originalEvent || {}).clipboardData) || {}).items || [];
                for (const item of items) {
                    if (item.kind === 'file') {
                        const blob = item.getAsFile();
                        const reader = new FileReader();
                        reader.onload = (f) => {
                            eventAggregator.publish(RightshipQiMessages.paste, { source: (<any>f.target).result, fileName: blob.name });
                        };
                        reader.readAsDataURL(blob);
                    }
                }
            }
        };

        this._subscriptions.push(eventAggregator.subscribe('show-date-picker', () => {
            this.showDatePicker = true;
        }));

        this._subscriptions.push(eventAggregator.subscribe('hide-date-picker', () => {
            this.showDatePicker = false;
        }));
    }
    
    attached() {
        document.addEventListener("click", this.documentClick);
        document.addEventListener("paste", this.paste);
        this._subscriptions.push(this.eventAggregator.subscribe("router:navigation:complete",
            () => {
                //Check if navigation was cancelled
                const routesList = this._router.routes.concat(this.routes);
                this.selectedRoute = Enumerable.from(routesList).where(x => x != null && x.name != null)
                    .firstOrDefault(x => this._router.currentInstruction && x.name == this._router.currentInstruction.config.name);

                this.bindingSignaler.signal('refresh-is-active');
                
                this.googleAnalyticsService.sendCustomGA4Event('page_view');
            }));

        this.dialogClientService.subscribeShow(this.showDialogueCallback);

        this._subscriptions.push(this.eventAggregator.subscribe("tab-changed", tab => {

            tab = (typeof tab.title == "function" ? tab.title() : tab.title);
            tab = this.stripHtml(tab);

            this.googleAnalyticsService.sendCustomGA4Event('page_view', window.location.pathname + ' | Tab:' + tab);
        }));

        setTimeout(() => {
            this.selectedRoute = Enumerable.from(this.routes).firstOrDefault(x => this._router.currentInstruction && x.name == this._router.currentInstruction.config.name);
            this.bindingSignaler.signal('refresh-is-active');
        });
    }

    public showDialogueCallback: (view, model, showCloseButton, fullscreen, classList: string) => void = (view) => {

        return new Promise(() => {
            this.googleAnalyticsService.sendCustomGA4Event(view);
        });
    }

    stripHtml(html) {
        // Create a new div element
        const temporalDivElement = document.createElement("div");
        // Set the HTML content with the providen
        temporalDivElement.innerHTML = DOMPurify.sanitize(html);
        // Retrieve the text property of the element (cross-browser support)
        return temporalDivElement.textContent || temporalDivElement.innerText || "";
    }

    detached() {
        document.removeEventListener("click", this.documentClick);
        document.removeEventListener("paste", this.paste);
        this.dialogClientService.unSubscribeShow(this.showDialogueCallback);
    }

    configureRouter(config, router: Router) {
        this._router = router;
        this.config = config;
        this.nonNavigationRoutes = this.routesService.getAttachmentViewerNonNavigationRoutes();
        config.title = "Rightship";
        config.options.pushState = true;
        config.options.hashChange = true;
        config.options.root = "/attachment-viewer?entityTypeId=:entityTypeId&id=:id";
        config.addPipelineStep("preActivate", ClaimsStep);
        this.routes = this.getRoutes();
        config.map(this.routes);
        config.mapUnknownRoutes({ redirect: '/attachment-viewer' });
        router.configure(config);
    }

    public getRoutes(): RouteConfig[] {
        return this.nonNavigationRoutes;
    }

    getRouteName(menuTab: MenuTab) {
        const routes = menuTab.routes.filter(x => !(x.isVisible == false));

        if (routes.length > 1) {
            return menuTab.title;
        }

        const route = routes[0];
        return route.title;
    }
}